import React, { useEffect, useState, useContext } from 'react';
import { Row, Col, message, Space, Card, Form, Button, Typography, Skeleton } from 'antd';
import { CustomInput, CustomSelect, CustomSlider, CustomTextArea } from 'components/custom';
import { saveSetting, getBotDetail } from 'api-services/setting';
import './scss/settings.scss';
import ChatBotContext from 'hooks/chat-bot-context';
import useLoading from 'hooks/use-loading';
import Helper from 'utilities/helper';
import Paragraph from 'antd/es/typography/Paragraph';
import eventEmitter from 'utilities/event-emitter';
import { useNavigate } from 'react-router';

const { Text } = Typography;

function GeneralSettings() {
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const [skeleton, setSkeleton] = useState(true);

  const [form] = Form.useForm();
  const { selectedBot } = useContext(ChatBotContext);
  const [requiredMark, setRequiredMarkType] = useState('optional');
  const [chatbotName, setChatbotName] = useState('');
  // Open AI
  const [basePrompt, setBasePrompt] = useState('');
  const [model, setModel] = useState('');
  const [temperature, setTemperature] = useState(0);
  const [generalLoading, setGeneralLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState('');
  const [botDetails, setBotDetails] = useState({});

  useEffect(() => {
    if(selectedBot) {
      getGeneralDetail();
    } else {
      navigate('/chatbots');
    }
  }, [selectedBot]);

  const getGeneralDetail = async () => {
    try {
      setSkeleton(true);
      if (selectedBot) {
        const result = await getBotDetail(selectedBot);
        if (result?.status === 200) {
          setBotDetails(result.data);
          setChatbotName(result.data.bot_name);
          setBasePrompt(result.data.prompt);
          setTemperature(result.data.temperature);
          setModel(result.data.model.model_source);
          setLastUpdate(result.data.embedding_creation_timestamp);
        }
      }
    } catch (e) {
      console.log(e);
    } finally {
      setSkeleton(false);
    }
  };
  const onRequiredTypeChange = ({ requiredMarkValue }) => {
    setRequiredMarkType(requiredMarkValue);
  };

  const getDataSize = () => {
    switch (botDetails.bot_source_type) {
    case 'xlsx':
    case 'xls':
    case 'csv':
      return `Columns: ${botDetails.columns} Rows: ${botDetails.rows}`;

    case 'pdf':
    case 'docx':
    case 'doc':
    default:
      return botDetails.characters_count + ' characters';
    }
  };

  const generalCardSubmit = async (openAI = false) => {
    if (chatbotName.trim()) {
      try {
        setLoading(true);
        setGeneralLoading(true);
        /* eslint-disable indent */
        const response = await saveSetting(
          selectedBot,
          openAI
            ? {
                prompt: basePrompt,
                temperature: temperature,
              }
            : {
                bot_id: selectedBot,
                bot_name: chatbotName,
              },
        );
        if (response?.data?.success) {
          // Process the response data and update the chat with the response message
          message.destroy();
          message.success('Data has been updated successfully');
          setGeneralLoading(false);
          eventEmitter.emit('botSelected', { id: selectedBot, bot_name: chatbotName });
        } else {
          // Handle error if the API request is unsuccessful
          setGeneralLoading(false);
        }
      } catch (error) {
        // Handle error if the API request encounters an exception
        console.error('API request error:', error);
        setGeneralLoading(false);
      } finally {
        setLoading(false);
      }
    } else {
      message.warning('Please provide bot name.');
    }
  };

  return (
    <div>
      <h2 className='page-title'>Bot Settings</h2>

      <Row>
        <Col span={24}>
          <Form
            id='bot-settings'
            form={form}
            layout='vertical'
            initialValues={{
              requiredMarkValue: requiredMark,
            }}
            onValuesChange={onRequiredTypeChange}
            requiredMark={requiredMark}>
            <Space size='middle' direction='vertical'>
              {skeleton && (
                <>
                  <Card>
                    <Skeleton active />
                  </Card>
                  <Card>
                    <Skeleton active />
                  </Card>
                </>
              )}

              {!skeleton && (
                <>
                  <Card>
                    <div className='card-header'>
                      <div className='card-header-title'>
                        <h3>General</h3>
                        <p>Enter your general details</p>
                      </div>
                      <Form.Item>
                        <Button
                          type='primary'
                          disabled={generalLoading}
                          loading={generalLoading}
                          onClick={() => generalCardSubmit(false)}>
                          Save
                        </Button>
                      </Form.Item>
                    </div>

                    <Row gutter={24} className='setting-tab'>
                      <Col span={8}>
                        <Form.Item className='copy-text' label='Chatbot ID' required>
                          <Text copyable>{selectedBot}</Text>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item label='Training data size' required>
                          <Text>{getDataSize()}</Text>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item label='Chatbot Name' required>
                          <CustomInput
                            placeholder='Enter chatbot name here'
                            id='chatbotName'
                            state={chatbotName}
                            setState={setChatbotName}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={24} className='setting-tab'>
                      <Col span={8}>
                        <Form.Item className='copy-text' label='Model Source' id='modelSource' required>
                          <Text>{model}</Text>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item label='Last Trained At' required>
                          <Text>{Helper.convertServerDate(lastUpdate)}</Text>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item label='Temperature' required hidden>
                          <Text>{temperature}</Text>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>

                  <Card>
                    <div className='card-header'>
                      <div className='card-header-title'>
                        <h3>Open AI</h3>
                        <p>Change Open AI settings</p>
                      </div>
                      <Form.Item>
                        <Button
                          type='primary'
                          disabled={generalLoading}
                          loading={generalLoading}
                          onClick={() => generalCardSubmit(true)}>
                          Save
                        </Button>
                      </Form.Item>
                    </div>

                    <Row gutter={24} className='setting-tab'>
                      <Col span={12}>
                        <Form.Item shouldUpdate label='Base Prompt (system message)' required>
                          <CustomTextArea
                            id='basePrompt'
                            rows={8}
                            placeholder='I want you to act as a ...'
                            state={basePrompt}
                            setState={setBasePrompt}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item shouldUpdate label='Temperature' required style={{ marginBlockEnd: 5 }}>
                          <CustomSlider
                            dots
                            min={0}
                            max={1}
                            // marks={marks}
                            step={0.1}
                            state={temperature}
                            setState={setTemperature}
                          />
                          <Paragraph
                            type='info'
                            style={{
                              marginBlockStart: 40,
                            }}>
                            0 is Reserved, 1 is Creative
                          </Paragraph>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label='Model' required hidden>
                          <CustomSelect
                            state={model}
                            setState={setModel}
                            style={{ width: 200 }}
                            defaultValue='gpt-3.5-turbo'
                            // options={modelOptionsList}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                </>
              )}
            </Space>
          </Form>
        </Col>
      </Row>
    </div>
  );
}

export default GeneralSettings;
